import { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { colors } from '@karnott/colors';
import { UIHooks } from '@karnott/hooks';
import { loadState } from '../api/localStorage';
import { useTracksHighlightStyle } from '../components/map/assets/Tracks/effects';
import { USER_CONSTANTS, UserUnits } from '../constants';
import { REPORT_EQUIPMENT_SORTING, ReportEquipmentSort } from '../constants/reportConstants';
import { useStoredState } from '../effects';
import {
  selectIsDeneigementContext,
  selectIsViticultureContext,
  selectNumberOfDemoClusters,
} from '../selectors/clusters';
import { selectUserUnits } from '../selectors/users';
import { EquipmentId } from '../slices/equipments';
import { ObservationId, selectFocusedObservationId } from '../slices/observations/general';
import { selectEditableDemo } from '../slices/users';
import { UserId } from '../slices/users';
import { useAppSelector } from '../store/hooks';
import { noop } from '../utils';
import { localStorageKeys } from '../utils/storage';
import { handleDateURLParams, initDatesFromURL } from '../utils/url';

type TrackStyle = { strokeColor: string; color: string; pane: string; weight: number };

type Context = {
  blurDriver: () => void;
  blurEquipment: () => void;
  blurWorksite: () => void;
  closeDemoModal: () => void;
  demoAccount: boolean;
  demoModalOpened: boolean;
  deneigementAccount: boolean;
  editableDemoAccount: boolean;
  focusDriver: (id?: UserId) => void;
  focusEquipment: (id?: EquipmentId) => void;
  focusWorksite: (id?: EquipmentId) => void;
  focusedDriverId: UserId | null;
  focusedEquipmentId: EquipmentId | null;
  focusedObservationId?: ObservationId;
  focusedWorksiteId: EquipmentId | null;
  fromDate: Moment | null;
  hideDriverTooltip: boolean;
  hideEquipmentTooltip: boolean;
  modifyDates: (fromDate: Moment | null, toDate: Moment | null) => void;
  openDemoModal: () => void;
  reportEquipmentSort: ReportEquipmentSort | '';
  setHideDriverTooltip: Dispatch<SetStateAction<boolean>>;
  setHideEquipmentTooltip: Dispatch<SetStateAction<boolean>>;
  setReportEquipmentSort: (sort: ReportEquipmentSort) => void;
  setTrackStyle: ((feature: unknown) => TrackStyle)[];
  showMarkerClusters: boolean;
  showParcelNames: boolean;
  toDate: Moment | null;
  toggleMarkerClusters: () => void;
  toggleParcelNames: () => void;
  trackOverStyle: TrackStyle;
  units: UserUnits;
  viticultureAccount: boolean;
};

export const UIContext = createContext<Context>({
  blurDriver: noop,
  blurEquipment: noop,
  blurWorksite: noop,
  closeDemoModal: noop,
  demoAccount: false,
  demoModalOpened: false,
  deneigementAccount: false,
  editableDemoAccount: false,
  focusDriver: noop,
  focusEquipment: noop,
  focusWorksite: noop,
  focusedDriverId: null,
  focusedEquipmentId: null,
  focusedObservationId: undefined,
  focusedWorksiteId: null,
  fromDate: null,
  hideDriverTooltip: false,
  hideEquipmentTooltip: false,
  modifyDates: noop,
  openDemoModal: noop,
  reportEquipmentSort: '',
  setHideDriverTooltip: noop,
  setHideEquipmentTooltip: noop,
  setReportEquipmentSort: noop,
  setTrackStyle: [],
  showMarkerClusters: false,
  showParcelNames: false,
  toDate: null,
  toggleMarkerClusters: noop,
  toggleParcelNames: noop,
  trackOverStyle: { strokeColor: '', color: '', pane: '', weight: 0 },
  units: {
    area: USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
    distance: USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
    time: USER_CONSTANTS.UNITS.TIME.STANDARD,
  },
  viticultureAccount: false,
});

// the provider is built before the store hydrates, so the user id comes straight from the persisted state
const userId = (loadState() as { user?: { id?: UserId } } | undefined)?.user?.id;

function readStoredObject<T>(key: string) {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return typeof parsed === 'object' && parsed !== null ? (parsed as T) : null;
  } catch {
    return null;
  }
}

function getInitialDatesInLocalStorage(): { fromDate: Moment | null; toDate: Moment | null } {
  const noDates = { fromDate: null, toDate: null };

  // discard the dates only on a stored mismatch: an absent entry predates the tracking and stays usable
  const dateMeta = readStoredObject<{ setBy?: UserId }>(localStorageKeys.dateRangeMeta);
  if (dateMeta && dateMeta.setBy !== userId) {
    return noDates;
  }

  const inStorage = readStoredObject<{ days?: number; fromDate?: string; toDate?: string }>(localStorageKeys.dateRange);
  if (!inStorage) {
    return noDates;
  }
  if (typeof inStorage.days === 'number') {
    return {
      fromDate: moment().subtract(inStorage.days, 'days').startOf('day'),
      toDate: moment().endOf('day'),
    };
  }
  return {
    fromDate: typeof inStorage.fromDate === 'string' ? moment(inStorage.fromDate) : null,
    toDate: typeof inStorage.toDate === 'string' ? moment(inStorage.toDate) : null,
  };
}

function storeDatesInLocalStorage(fromDate: Moment | null, toDate: Moment | null) {
  if (userId) {
    // store information on date setting
    const dateMeta = { setBy: userId, setOn: moment().format() };
    localStorage.setItem(localStorageKeys.dateRangeMeta, JSON.stringify(dateMeta));
  }

  // store day delta from today if toDate is today
  if (moment.isMoment(toDate) && toDate.isSame(moment(), 'day') && moment.isMoment(fromDate)) {
    const dayCount = toDate.diff(fromDate, 'days');
    localStorage.setItem(localStorageKeys.dateRange, JSON.stringify({ days: dayCount }));
    return;
  }

  // else, store absolute days
  localStorage.setItem(
    localStorageKeys.dateRange,
    JSON.stringify({
      fromDate: moment.isMoment(fromDate) ? fromDate.format() : null,
      toDate: moment.isMoment(toDate) ? toDate.format() : null,
    }),
  );
}

const initialDatesInURL = initDatesFromURL();
const initialDatesInLocalStorage = getInitialDatesInLocalStorage();

export function UIProvider({ children }: { children: ReactNode }) {
  const focusedObservationId = useAppSelector(selectFocusedObservationId);
  const units = useAppSelector(selectUserUnits);
  const isThereDemoClusters = useAppSelector(selectNumberOfDemoClusters) > 0;
  const editableDemo = useAppSelector(selectEditableDemo);
  const isViticulture = useAppSelector(selectIsViticultureContext);
  const isDeneigement = useAppSelector(selectIsDeneigementContext);

  const [demoModalOpened, openDemoModal, closeDemoModal] = UIHooks.useOpenCloseState(false);

  const [fromDate, setFromDate] = useState<Moment | null>(
    initialDatesInURL.fromDate || initialDatesInLocalStorage.fromDate,
  );
  const [toDate, setToDate] = useState<Moment | null>(initialDatesInURL.toDate || initialDatesInLocalStorage.toDate);
  const [focusedEquipmentId, setFocusedEquipmentId] = useState<EquipmentId | null>(null);
  const [focusedDriverId, setFocusedDriverId] = useState<UserId | null>(null);
  const [focusedWorksiteId, setFocusedWorksiteId] = useState<EquipmentId | null>(null);
  const [showParcelNames, setShowParcelNames] = useStoredState(localStorageKeys.parcelsName, false);
  const [showMarkerClusters, setShowMarkerClusters] = useStoredState(localStorageKeys.showMarkerClusters, false);
  const [hideEquipmentTooltip, setHideEquipmentTooltip] = useState(false);
  const [hideDriverTooltip, setHideDriverTooltip] = useState(false);
  const [storedReportEquipmentSort, setSortForReportEquipment] = useStoredState<ReportEquipmentSort | ''>(
    localStorageKeys.reportEquipmentSort,
    '',
  );

  const focusEquipment = useCallback((id?: EquipmentId) => setFocusedEquipmentId(id ?? null), []);
  const blurEquipment = useCallback(() => setFocusedEquipmentId(null), []);
  const focusDriver = useCallback((id?: UserId) => setFocusedDriverId(id ?? null), []);
  const blurDriver = useCallback(() => setFocusedDriverId(null), []);
  const focusWorksite = useCallback((id?: EquipmentId) => setFocusedWorksiteId(id ?? null), []);
  const blurWorksite = useCallback(() => setFocusedWorksiteId(null), []);

  const reportEquipmentSort =
    Object.values(REPORT_EQUIPMENT_SORTING).find((sort) => sort === storedReportEquipmentSort) ?? '';

  const toggleParcelNames = useCallback(() => setShowParcelNames((shown) => !shown), [setShowParcelNames]);

  const toggleMarkerClusters = useCallback(() => setShowMarkerClusters((shown) => !shown), [setShowMarkerClusters]);

  const setReportEquipmentSort = useCallback(
    (sort: ReportEquipmentSort) => setSortForReportEquipment((current) => (current === sort ? '' : sort)),
    [setSortForReportEquipment],
  );

  const modifyDates = useCallback((from: Moment | null, to: Moment | null) => {
    handleDateURLParams(from, to);
    storeDatesInLocalStorage(from, to);
    setFromDate(from);
    setToDate(to);
  }, []);

  const setTrackStyle = useTracksHighlightStyle();

  const trackOverStyle = useMemo(
    () => ({
      strokeColor: colors('orange'),
      color: colors('orange'),
      pane: 'markerPane',
      weight: 3,
    }),
    [],
  );

  return (
    <UIContext.Provider
      value={{
        blurDriver,
        blurEquipment,
        blurWorksite,
        closeDemoModal,
        demoAccount: isThereDemoClusters && !editableDemo,
        demoModalOpened,
        deneigementAccount: isDeneigement,
        editableDemoAccount: isThereDemoClusters && !!editableDemo,
        focusDriver,
        focusEquipment,
        focusWorksite,
        focusedDriverId,
        focusedEquipmentId,
        focusedObservationId,
        focusedWorksiteId,
        fromDate,
        hideDriverTooltip,
        hideEquipmentTooltip,
        modifyDates,
        openDemoModal,
        reportEquipmentSort,
        setHideDriverTooltip,
        setHideEquipmentTooltip,
        setReportEquipmentSort,
        setTrackStyle,
        showMarkerClusters,
        showParcelNames,
        toDate,
        toggleMarkerClusters,
        toggleParcelNames,
        trackOverStyle,
        units,
        viticultureAccount: isViticulture,
      }}
    >
      {children}
    </UIContext.Provider>
  );
}
